import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { animated, useSpring } from "@react-spring/web";
import { useNavigate } from "react-router-dom";

import InfiniteScroll from "src/components/infiniteScroll";
import { TabBar } from "src/components/tabbar";
import PermissionDenined from "src/components/permissionDenied";
import { CourseCard, PlaceholderCourseCard } from "./card";
import { SearchBar } from "src/components/input";
import SortByAction from "src/components/sortByAction";
import { LearningsSidebar } from "./components/sideBar";

import { fetchMyCourses } from "src/redux/actions/coaching/learners";
import { LearningRecordSummary } from "src/types/coaching/learners";

import userRoleService from "src/utils/userRoles.service";

import { SortOrder } from "src/types/common";

import _ from "lodash";
import cx from "classnames";
import styles from "./learnings.module.scss";

type CourseStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

const LEARNINGS_SORT_OPTIONS = [
  { label: "name", value: "name" },
  { label: "due date", value: "dueDate" },
  { label: "date assigned", value: "dateAssigned" },
];

const parseRecordDate = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const t = new Date(value as string | number | Date).getTime();
  return Number.isNaN(t) ? null : t;
};

const sortLearningRecords = (
  records: LearningRecordSummary[],
  sortBy: string,
  orderBy: SortOrder,
): LearningRecordSummary[] => {
  const dir = orderBy === SortOrder.ASC ? 1 : -1;
  const next = [...records];

  next.sort((a, b) => {
    if (sortBy === "name") {
      const na = String(_.get(a, "course.name", "")).toLowerCase();
      const nb = String(_.get(b, "course.name", "")).toLowerCase();
      const cmp = na.localeCompare(nb, undefined, { sensitivity: "base" });
      return cmp * dir;
    }

    if (sortBy === "dueDate") {
      const ta = parseRecordDate(_.get(a, "dueDate"));
      const tb = parseRecordDate(_.get(b, "dueDate"));
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return (ta - tb) * dir;
    }

    if (sortBy === "dateAssigned") {
      const ta =
        parseRecordDate(_.get(a, "createdDate")) ??
        parseRecordDate(_.get(a, "course.createdDate"));
      const tb =
        parseRecordDate(_.get(b, "createdDate")) ??
        parseRecordDate(_.get(b, "course.createdDate"));
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return (ta - tb) * dir;
    }

    return 0;
  });

  return next;
};

const COURSE_CATEGORIES = [
  {
    label: "assigned",
    status: "NOT_STARTED" as CourseStatus,
    key: "notStarted",
  },
  {
    label: "in progress",
    status: "IN_PROGRESS" as CourseStatus,
    key: "inProgress",
  },
  {
    label: "completed",
    status: "COMPLETED" as CourseStatus,
    key: "completed",
  },
];

const EMPTY_STATE_CONTENT: Record<
  CourseStatus,
  { title: string; description: string }
> = {
  NOT_STARTED: {
    title: "No learning assigned!",
    description: "You currently don’t have any learnings assigned.",
  },
  IN_PROGRESS: {
    title: "No learnings in progress!",
    description: "Your active learnings will appear here once started.",
  },
  COMPLETED: {
    title: "No learnings completed!",
    description: "Completed learnings will appear here",
  },
};

interface CourseDataProps {
  loading: boolean;
  loaded: boolean;
  courses: LearningRecordSummary[];
  hasMoreItems: boolean;
  nextPage: number | null;
  total: number;
  // eslint-disable-next-line no-unused-vars
  fetchCoursesForPage: (page: number) => void;
}

const useCourses = (status: string, query?: string | null): CourseDataProps => {
  const dispatch = useDispatch();

  const { courses, loading, loaded, hasMoreItems, nextPage, total } = useSelector((state) => {
    const key = COURSE_CATEGORIES.find((item) => item.status === status)?.key;
    const coursesState = _.get(state, `learners.courses.${key}`);

    const courses = _.get(coursesState, 'items') || [] as LearningRecordSummary[];
    const loading = _.get(coursesState, 'loading') || false;
    const loaded = _.get(coursesState, 'loaded') || false;
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems") || false;

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) nextPage = null;

    return { courses, loading, loaded, hasMoreItems, nextPage, total };
  }, _.isEqual);

  const fetchCoursesForPage = (page: number): void => {
    dispatch(fetchMyCourses({
      status,
      page,
      limit: 5,
      ...(query ? { query } : {}),
    }));
  };

  useEffect(() => {
    fetchCoursesForPage(0);
  }, [query]);

  return { loading, loaded, courses, hasMoreItems, nextPage, total, fetchCoursesForPage };
};

interface CategorySectionProps {
  label: string;
  status: CourseStatus;
  sort: { sortBy: string; orderBy: SortOrder };
  query?: string | null;
}

const CategorySection: React.FC<CategorySectionProps> = ({ label, status, sort, query }) => {
  const navigate = useNavigate();
  const { loading, courses, hasMoreItems, nextPage, fetchCoursesForPage } = useCourses(status, query);

  const sortedCourses = React.useMemo(
    () => sortLearningRecords(courses, sort.sortBy, sort.orderBy),
    [courses, sort.sortBy, sort.orderBy],
  );

  const hasSearchQuery = _.size(_.trim(query || "")) > 0;

  const handleLoadMoreCourses = () => {
    if (nextPage !== null) fetchCoursesForPage(nextPage);
  };

  const handleClick = (record: LearningRecordSummary) => {
    if(record.course?.entityType === "simulation") {
      navigate(`/coaching/simulations/${record.id}`);
    } else if(record.course?.entityType === "course") {
      navigate(`/coaching/courses/${record.id}`);
    } else if(record.course?.entityType === "podcast") {
      navigate(`/coaching/podcasts/${record.id}`);
    }
  }

  return (
    <div className={styles.contentDetails}>
      <div className={styles.left}>
      </div>
      <div className={styles.right}>
        <div className={styles.listHost}>
          <InfiniteScroll
            key={`${status}-${query ?? ""}`}
            className={styles.list}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMoreCourses}
            loader={() =>
              _.times(_.size(sortedCourses) === 0 ? 4 : 1).map((i) => <PlaceholderCourseCard key={i} />)
            }
          >
            {_.size(sortedCourses) === 0 && !loading && (
              <div className={styles.emptyMessage} role="status">
                <h2 className={styles.title}>
                  {hasSearchQuery
                    ? "no results found"
                    : EMPTY_STATE_CONTENT[status].title}
                </h2>
                <p className={styles.subText}>
                  {hasSearchQuery
                    ? "No learnings match your search."
                    : EMPTY_STATE_CONTENT[status].description}
                </p>
              </div>
            )}
            {sortedCourses.map((course, index) => (
              <CourseCard key={course.id || index} course={course} status={status} onClick={handleClick} />
            ))}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
};

interface LearningTabPanelProps {
  index: number;
  selected: number;
  label: string;
  status: CourseStatus;
  sort: { sortBy: string; orderBy: SortOrder };
  query?: string | null;
}

const LearningTabPanel: React.FC<LearningTabPanelProps> = ({
  index,
  selected,
  label,
  status,
  sort,
query}) => {
  const direction = selected > index ? -1 : 1;

  const transitions = useSpring({
    opacity: selected === index ? 1 : 0,
    transform:
      selected === index
        ? "translateX(0%)"
        : `translateX(${direction * 100}%)`,
    config: { tension: 180, friction: 28 },
  });

  return (
    <animated.div
      className={styles.tabPanel}
      style={{
        ...transitions,
        pointerEvents: selected === index ? "auto" : "none",
        height: selected === index ? "auto" : 0,
        overflow: selected === index ? "visible" : "hidden",
      }}
    >
    <CategorySection status={status} label={label} sort={sort} query={query} />
    </animated.div>
  );
};

const Learnings: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [sortState, setSortState] = useState({ sortBy: "dateAssigned", orderBy: SortOrder.DESC });
  const canAccessGamification = userRoleService.canAccessGamification();

  const courseTotals = useSelector((state: any) =>
    COURSE_CATEGORIES.map(({ key }) =>
      _.get(state, `learners.courses.${key}.pagination.totalRecordsCount`, 0)
    ), _.isEqual
  );

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.bodyWrapper}>
        <div className={styles.mainColumn}>
          <div className={styles.searchBarHost}>
            <SearchBar
              query={searchQuery}
              onSearch={setSearchQuery}
              placeholder="Search by learning name..."
              className={styles.learningsSearchBar}
            />
          </div>
          <div className={styles.tabRow}>
            <TabBar
              className={styles.tabs}
              innerClass={styles.tabsInner}
              active={activeTab}
              gap={40}
              onSelectionChange={setActiveTab}
            >
              {COURSE_CATEGORIES.map(({ label }, index) => (
                <div key={label} className={cx(styles.tabItem, { [styles.active]: activeTab === index })}>
                  <div className={styles.tabCount}>{(courseTotals[index] || 0).toString().padStart(2, "0")}</div>
                  <div className={styles.tabLabel}>{label}</div>
                </div>
              ))}
            </TabBar>
            {/* <SortByAction
              value={sortState}
              onChange={setSortState}
              sortOptions={LEARNINGS_SORT_OPTIONS}
              classnames={styles.sortByAction}
            /> */}
          </div>
          <div className={styles.content}>
            <div className={styles.tabPanels}>
              {COURSE_CATEGORIES.map(({ label, status }, index) => (
                <LearningTabPanel
                  key={status}
                  index={index}
                  selected={activeTab}
                  status={status}
                  label={label}
                  sort={sortState}
                  query={searchQuery}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.sidebarColumn}>
          <LearningsSidebar
            hasGamificationAccess= {canAccessGamification}
            onOpenRewardsCatalog={() => navigate("/coaching/rewards")}
          />
        </div>
      </div>
    </div>
  );
};
export { Learnings };